import React, { useState } from "react";
import {
  Box,
  Typography,
  Button,
  ButtonGroup,
  Container,
  Grid,
  Card,
  CardContent,
  Link,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import { styled, keyframes } from "@mui/system";
import CheckIcon from "@mui/icons-material/Check";
import LocalOfferIcon from "@mui/icons-material/LocalOffer";
import { isMobile } from "react-device-detect";
import { leerBaul, limpiarBaulSiExpirado } from "../../../services/referralBaul";

// ─── Animación manito indicadora ─────────────────────────────────────────────
const handBounce = keyframes`
  0%   { transform: translateY(0px)  rotate(225deg);  }
  20%  { transform: translateY(-7px) rotate(213deg);  }
  40%  { transform: translateY(0px)  rotate(225deg);  }
  60%  { transform: translateY(-7px) rotate(237deg);  }
  80%  { transform: translateY(0px)  rotate(225deg);  }
  100% { transform: translateY(0px)  rotate(225deg);  }
`;
const PricingCard = styled(Card)(({ theme }) => ({
  padding: "24px",
  borderRadius: "16px",
  boxShadow: "0 4px 16px rgba(0, 0, 0, 0.1)",
  border: "1px solid #e0e0e0",
  position: "relative",
  height: "100%",
  display: "flex",
  flexDirection: "column",
  background: "#ffffff",
  transition: "all 0.3s ease",
  "&:hover": {
    transform: "translateY(-4px)",
    boxShadow: "0 8px 24px rgba(0, 0, 0, 0.15)",
  },
}));

const ActionButton = styled(Button)(({ theme }) => ({
  backgroundColor: "#4669E8",
  color: "white",
  padding: "8px 24px",
  borderRadius: "8px",
  textTransform: "none",
  fontFamily: "Quicksand-Bold",
  fontSize: "16px",
  width: "100%",
  transition: "all 0.3s ease",
  "&:hover": {
    backgroundColor: "#2952CC",
  },
}));

const FeatureItem = styled(Box)(() => ({
  display: "flex",
  alignItems: "center",
  marginBottom: "12px",
  gap: "8px",
}));

const CheckIconStyled = styled(CheckIcon)(() => ({
  color: "#4CAF50",
  fontSize: isMobile ? "17px" : "20px",
}));

const PriceText = styled(Typography)(({ theme }) => ({
  fontFamily: "Poppins-SemiBold",
  fontSize: "38px",
  color: "#09090a",
  [theme.breakpoints.down("md")]: {
    fontSize: "32px",
  },
}));

const Planes = () => {
  const theme = useTheme();
  const [toggleAnual, setToggleAnual] = useState(false);
  const isDesktop = useMediaQuery(theme.breakpoints.up('md'));

  // ── Lectura on-demand del baúl (síncrona, fresca cada render) ──────────
  limpiarBaulSiExpirado();
  const baul = leerBaul();
  const referralMessage = baul?.mensajeCorto || null;

  const PRECIO_MENSUAL_SIN_IVA = 19990;
  const IVA = 1.19;
  const DESCUENTO_ANUAL = 0.75;

  const SCALE_MOBILE = 1.065;
  const SCALE_DESKTOP = 1.065;

  const precioMensualConIVA = Math.round(PRECIO_MENSUAL_SIN_IVA * IVA);
  const precioAnualSinDescuento = PRECIO_MENSUAL_SIN_IVA * 12;
  const precioAnualConDescuento = Math.round(precioAnualSinDescuento * DESCUENTO_ANUAL);
  const precioAnualConIVA = Math.round(precioAnualConDescuento * IVA);

  const formatPrice = (price) => {
    return `$${price.toLocaleString('es-CL')}`;
  };

  const planGratuito = {
    name: "Gratuito",
    price: "$0",
    subtitle: "gratis para siempre",
    buttonText: "Registrarte",
    description: "",
    features: [
      "Perfil de agendamiento web",
      "Pagos con tarjeta",
      "Evaluaciones de pacientes en tu perfil web",
      "Agenda online, presencial y a domicilio",
      "Servicios ilimitados",
      "Notificación de agendamientos",
      "Ficha clínica de pacientes",
      "Adjuntar archivos a la ficha",
      "Historial de citas por paciente",
      "Marcar citas como pagadas manualmente",
      "Códigos de descuento",
      "Link de videollamada",
      "Integración con Google Calendar Básico",
      "Bloqueo de horarios",
      "Presencia en buscador Clinifica.com",
      "Reportes y estadísticas básicos",
      "5 GB de almacenamiento"
    ],
    note: "",
  };

  const featuresPro = [
    "Todas las funciones del plan Gratuito",
    "Envío de recordatorios ilimitados por WhatsApp y email",
    "Envío de confirmación de asistencia por WhatsApp y email",
    "Envío automático de evaluaciones por WhatsApp y email",
    "Envío de cobros por WhatsApp y email de Clinifica",
    "Emisión boleta de honorarios",
    "Transcripción de sesiones con IA clínica",
    "Reportes y estadísticas avanzados",
    "Integración con Google Calendar Avanzado",
    "Venta de eventos ilimitados",
    "Venta de recursos digitales ilimitados",
    "256 GB de almacenamiento"
  ];

  const planProMensual = {
    name: "Pro",
    price: formatPrice(precioMensualConIVA),
    priceWithoutTax: `${formatPrice(PRECIO_MENSUAL_SIN_IVA)} + IVA/mes`,
    buttonText: "Registrarte",
    description: "",
    features: featuresPro,
    note: "",
    promo: referralMessage || "primer mes a $990"
  };

  const planProAnual = {
    name: "Pro",
    price: formatPrice(precioAnualConIVA),
    priceWithoutTax: `${formatPrice(precioAnualConDescuento)} + IVA/año`,
    buttonText: "Registrarte",
    description: "",
    features: featuresPro,
    note: "",
    promoAnual: true
  };

  const planPro = toggleAnual ? planProAnual : planProMensual;
  const plans = [planGratuito, planPro];

  return (
    <Container maxWidth="lg">
      {/* Título */}
      <Box sx={{ textAlign: { xs: "center", md: "center" }, mb: 3 }}>
        <Typography
          sx={{
            fontFamily: "Poppins-SemiBold",
            fontSize: { xs: "24px", md: "36px" },
            color: "#141414",
            mb: 2,
          }}
        >
          Nuestros Planes
        </Typography>
      </Box>

      {/* Selector Mensual/Anual */}
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          mb: 4,
        }}
      >
        <ButtonGroup
          sx={{
            backgroundColor: "#f0f0f0",
            border: "none",
            borderRadius: 2,
            height: "40px",
            padding: "4px",
            "& .MuiButton-root": {
              borderRadius: "8px !important",
              border: "none !important",
              minWidth: "auto",
              px: 2.5,
              py: 1,
              whiteSpace: "nowrap",
            }
          }}
        >
          <Button
            onClick={() => setToggleAnual(false)}
            sx={{
              fontFamily: "Quicksand-Regular",
              fontSize: { xs: "14px", md: "16px" },
              color: "#09090a",
              textTransform: "none",
              backgroundColor: !toggleAnual ? "#FFFFFF" : "transparent",
              fontWeight: !toggleAnual ? "600" : "400",
              "&:hover": {
                backgroundColor: !toggleAnual ? "#FFFFFF" : "#EEEEEE",
              }
            }}
          >
            mensual
          </Button>
          <Button
            onClick={() => setToggleAnual(true)}
            sx={{
              position: "relative",
              fontFamily: "Quicksand-Bold",
              fontSize: { xs: "14px", md: "16px" },
              textTransform: "none",
              backgroundColor: toggleAnual ? "#FFFFFF" : "transparent",
              display: "flex",
              alignItems: "center",
              gap: 0.5,
              "&:hover": {
                backgroundColor: toggleAnual ? "#FFFFFF" : "#EEEEEE",
              }
            }}
          >
            <Box
              sx={{
                position: "absolute",
                right: { xs: "-10px", md: "-15px" },
                top: "-24px",
                fontSize: "1.6rem",
                lineHeight: 1,
                animation: `${handBounce} 0.85s ease-in-out infinite`,
                userSelect: "none",
                pointerEvents: "none",
                zIndex: 2,
              }}
            >
              👆
            </Box>
            <Typography
              component="span"
              sx={{
                fontFamily: "Quicksand-Bold",
                fontSize: { xs: "14px", md: "16px" },
                fontWeight: toggleAnual ? "600" : "400",
                ...(toggleAnual ? {
                  color: "#1976D2"
                } : {
                  background: "linear-gradient(90deg, #4B5563 0%, #4B5563 40%, #F3F4F6 50%, #4B5563 60%, #4B5563 100%)",
                  backgroundSize: "200% 100%",
                  WebkitBackgroundClip: "text",
                  WebkitTextFillColor: "transparent",
                  backgroundClip: "text",
                  animation: "shimmerText 2s ease-in-out infinite",
                }),
                "@keyframes shimmerText": {
                  "0%": { backgroundPosition: "200% 0" },
                  "100%": { backgroundPosition: "-200% 0" }
                }
              }}
            >
              anual
            </Typography>
            <Typography
              component="span"
              sx={{
                fontFamily: "Quicksand-Bold",
                fontSize: { xs: "14px", md: "16px" },
                fontWeight: toggleAnual ? "600" : "400",
                ...(toggleAnual ? {
                  color: "#1976D2",
                  animation: "brilloTexto 3s ease-in-out infinite",
                  "@keyframes brilloTexto": {
                    "0%": { textShadow: "0 0 6px rgba(25, 118, 210, 0.2)" },
                    "50%": { textShadow: "0 0 6px rgba(25, 118, 210, 0.4), 0 0 25px rgba(25, 118, 210, 0.2)" },
                    "100%": { textShadow: "0 0 6px rgba(25, 118, 210, 0.2)" }
                  }
                } : {
                  background: "linear-gradient(90deg, #4B5563 0%, #4B5563 40%, #F3F4F6 50%, #4B5563 60%, #4B5563 100%)",
                  backgroundSize: "200% 100%",
                  WebkitBackgroundClip: "text",
                  WebkitTextFillColor: "transparent",
                  backgroundClip: "text",
                  animation: "shimmerText 2s ease-in-out infinite",
                }),
                transition: "all 0.3s ease",
                "@keyframes shimmerText": {
                  "0%": { backgroundPosition: "200% 0" },
                  "100%": { backgroundPosition: "-200% 0" }
                }
              }}
            >
              (3 meses gratis)
            </Typography>
          </Button>
        </ButtonGroup>
      </Box>

      {/* Cards de Planes */}
      <Grid container spacing={4} justifyContent="center" alignItems="stretch">
        {plans.map((plan, index) => (
          <Grid
            item
            xs={12}
            md={6}
            key={index}
            sx={{
              display: "flex",
              order: { xs: index === 0 ? 2 : 1, md: index + 1 },
              // 👇 ÚNICO CAMBIO: fuerza ancho idéntico en MD para todas las cards
              flex: { md: "0 0 360px" },   // 👈 cambia este px a mano
              maxWidth: { xs: "360px", md: "360px" },    // 👈 mismo valor
              minWidth: { md: "360px" },    // 👈 mismo valor
              ...(index === 1 && {
                mt: { xs: 3, md: 0 },
                mb: { xs: 3, md: 0 }
              })
            }}
          >
            <PricingCard sx={{ width: "100%" }}>
              <CardContent sx={{ p: 0, "&:last-child": { pb: 0 }, display: "flex", flexDirection: "column", height: "100%" }}>
                {/* Sección superior: Precio y Botón */}
                <Box sx={{ minHeight: "200px" }}>
                  <Box sx={{ mb: 2 }}>
                    <Typography
                      sx={{
                        fontFamily: "Quicksand-Regular",
                        fontSize: "14px",
                        color: "#666",
                      }}
                    >
                      Plan
                    </Typography>
                    <Typography
                      variant="h5"
                      sx={{
                        fontFamily: "Quicksand-Bold",
                        fontSize: "24px",
                        color: "#0A56A2",
                        mb: 2,
                      }}
                    >
                      {plan.name}
                    </Typography>

                    {/* Precio */}
                    <Box sx={{ mb: 2 }}>
                      {plan.name === "Gratuito" ? (
                        <>
                          <PriceText>{plan.price}</PriceText>
                          <Typography
                            sx={{
                              fontFamily: "Quicksand-Regular",
                              fontSize: { xs: "15px", md: "18px" },
                              color: "#09090a",
                              mt: 0.5
                            }}
                          >
                            {plan.subtitle}
                          </Typography>
                        </>
                      ) : (
                        <Box>
                          <Box sx={{ display: "flex", alignItems: "baseline", gap: 0.5 }}>
                            <PriceText>{plan.price}</PriceText>
                            <Typography
                              component="span"
                              sx={{
                                fontFamily: "Quicksand-Regular",
                                fontSize: { xs: "19px", md: "22px" },
                                color: "#09090a",
                                ml: 0.5
                              }}
                            >
                              {toggleAnual ? "/año" : "/mes"}
                            </Typography>
                          </Box>
                          {toggleAnual ? (
                            <Box sx={{ display: "flex", alignItems: "center", mt: 0.5, gap: 1 }}>
                              <Typography
                                sx={{
                                  fontFamily: "Quicksand-Regular",
                                  fontSize: { xs: "15px", md: "18px" },
                                  color: "#09090a",
                                  textDecoration: "line-through"
                                }}
                              >
                                {formatPrice(precioAnualSinDescuento)} + IVA/año
                              </Typography>
                              <Typography
                                sx={{
                                  fontFamily: "Quicksand-Bold",
                                  fontSize: { xs: "15px", md: "18px" },
                                  color: "#1D4ED8"
                                }}
                              >
                                ahorras 25%
                              </Typography>
                            </Box>
                          ) : (
                            <Typography
                              sx={{
                                fontFamily: "Quicksand-Regular",
                                fontSize: { xs: "15px", md: "18px" },
                                color: "#09090a",
                                mt: 0.5
                              }}
                            >
                              {plan.priceWithoutTax}
                            </Typography>
                          )}
                        </Box>
                      )}
                    </Box>
                  </Box>

                  {/* Botón de acción */}
                  <Box sx={{ mb: 2 }}>
                    <Link
                      href="/pro/registro"
                      underline="none"
                      style={{ textDecoration: "none" }}
                      onClick={() => {
                        if (window.fbq) window.fbq('trackCustom', 'inicioregistro');
                      }}
                    >
                      <ActionButton variant="contained">
                        {plan.buttonText}
                      </ActionButton>
                    </Link>
                  </Box>
                </Box>

                {/* Promoción mensual */}
                {plan.promo && !toggleAnual && (
                  <Box sx={{ mb: 2, mt: 1 }}>
                    <Box
                      sx={{
                        border: "1px dashed #3B82F6",
                        borderRadius: 4,
                        py: 1.5,
                        px: 2,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        gap: 1,
                        background: "linear-gradient(-45deg, #EFF6FF, #DBEAFE, #F0F9FF, #E0F2FE, #EFF6FF)",
                        backgroundSize: "400% 400%",
                        animation: "blueCouponShimmer 8s ease-in-out infinite",
                        "@keyframes blueCouponShimmer": {
                          "0%": { backgroundPosition: "0% 50%" },
                          "50%": { backgroundPosition: "100% 50%" },
                          "100%": { backgroundPosition: "0% 50%" }
                        }
                      }}
                    >
                      <LocalOfferIcon sx={{ fontSize: 18, color: "#3B82F6" }} />
                      <Typography
                        sx={{
                          fontFamily: "Quicksand-Bold",
                          fontSize: { xs: "14px", md: "16px" },
                          color: "#1D4ED8",
                          textAlign: "center"
                        }}
                      >
                        {plan.promo}
                      </Typography>
                    </Box>
                  </Box>
                )}

                {/* Promoción anual */}
                {plan.name === "Pro" && toggleAnual && (
                  <Box sx={{ mb: 2, mt: 1 }}>
                    <Box
                      sx={{
                        border: "1px dashed #3B82F6",
                        borderRadius: 4,
                        py: 1.5,
                        px: 2,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        gap: 1,
                        background: "linear-gradient(-45deg, #EFF6FF, #DBEAFE, #F0F9FF, #E0F2FE, #EFF6FF)",
                        backgroundSize: "400% 400%",
                        animation: "blueCouponShimmer 8s ease-in-out infinite",
                        "@keyframes blueCouponShimmer": {
                          "0%": { backgroundPosition: "0% 50%" },
                          "50%": { backgroundPosition: "100% 50%" },
                          "100%": { backgroundPosition: "0% 50%" }
                        }
                      }}
                    >
                      <LocalOfferIcon sx={{ fontSize: 18, color: "#3B82F6" }} />
                      <Typography
                        sx={{
                          fontFamily: "Quicksand-Bold",
                          fontSize: { xs: "14px", md: "16px" },
                          color: "#1D4ED8",
                          textAlign: "center"
                        }}
                      >
                        Plan anual con 25% de dcto
                      </Typography>
                    </Box>
                  </Box>
                )}

                {/* Descripción */}
                {plan.description && (
                  <Typography
                    sx={{
                      fontFamily: "Quicksand-Bold",
                      fontSize: "14px",
                      color: "#09090a",
                      mb: 2,
                      textAlign: "center"
                    }}
                  >
                    {plan.description}
                  </Typography>
                )}

                {/* Lista de características */}
                <Box sx={{ flexGrow: 1, mb: 2 }}>
                  {plan.features.map((feature, featureIndex) => (
                    <FeatureItem key={featureIndex}>
                      <CheckIconStyled />
                      <Typography
                        sx={{
                          fontFamily: "Quicksand-Regular",
                          fontSize: isMobile ? "13px" : "14px",
                          color: "#09090a",
                          lineHeight: 1.4,
                        }}
                      >
                        {feature}
                      </Typography>
                    </FeatureItem>
                  ))}
                </Box>

                {/* Nota del costo de pasarela */}
                {plan.note && (
                  <Box sx={{ px: 0, mb: -1 }}>
                    <Typography
                      sx={{
                        fontFamily: "Quicksand-Regular",
                        fontSize: { xs: "12px", md: "14px" },
                        color: "#09090a",
                        fontStyle: "italic",
                        textAlign: "center"
                      }}
                    >
                      {plan.note}
                    </Typography>
                  </Box>
                )}

              </CardContent>
            </PricingCard>
          </Grid>
        ))}
      </Grid>
    </Container>
  );
};

export default Planes;
